import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Lock, RefreshCw, LogOut, TrendingUp, Home as HomeIcon, Car, Building2, Loader2, Plus, Minus } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY = "bellani_admin_pw";

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: accent + "1a" }}>
          <Icon size={22} style={{ color: accent }} />
        </div>
        <div>
          <div className="text-2xl font-extrabold text-slate-700 leading-none">{value}</div>
          <div className="text-xs text-slate-400 mt-1">{label}</div>
        </div>
      </div>
    </div>
  );
}

export default function Admin() {
  const [pw, setPw] = useState(sessionStorage.getItem(KEY) || "");
  const [authed, setAuthed] = useState(!!sessionStorage.getItem(KEY));
  const [input, setInput] = useState("");
  const [overview, setOverview] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async (password) => {
    setLoading(true);
    try {
      const headers = { "X-Admin-Password": password };
      const [o, h] = await Promise.all([
        axios.get(`${API}/admin/overview`, { headers }),
        axios.get(`${API}/admin/sync-history`, { headers, params: { limit: 50 } }),
      ]);
      setOverview(o.data);
      setHistory(h.data || []);
    } catch (e) {
      if (e?.response?.status === 401) {
        toast.error("Sessão expirada. Faça login novamente.");
        sessionStorage.removeItem(KEY);
        setAuthed(false);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authed && pw) load(pw);
  }, [authed, pw, load]);

  const doLogin = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/login`, { password: input });
      sessionStorage.setItem(KEY, input);
      setPw(input);
      setAuthed(true);
      toast.success("Bem-vindo(a)!");
    } catch (e) {
      toast.error("Senha inválida.");
    }
  };

  const logout = () => {
    sessionStorage.removeItem(KEY);
    setAuthed(false);
    setPw("");
    setInput("");
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      await axios.post(`${API}/sync`);
      toast.success("Sincronização iniciada! Atualize em alguns segundos.");
      setTimeout(() => load(pw), 12000);
    } catch (e) {
      toast.error("Falha ao iniciar sincronização.");
    } finally {
      setTimeout(() => setSyncing(false), 12000);
    }
  };

  const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

  if (!authed) {
    return (
      <main className="max-w-md mx-auto px-5 pt-24 pb-24 fade-in">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
          <div className="flex flex-col items-center mb-6">
            <div className="h-14 w-14 rounded-2xl bg-[#faf1d6] flex items-center justify-center mb-3">
              <Lock className="text-[#C9A227]" size={26} />
            </div>
            <h1 className="text-xl font-extrabold text-slate-700">Painel do Robô</h1>
            <p className="text-sm text-slate-400 mt-1">Área restrita — informe a senha</p>
          </div>
          <form onSubmit={doLogin} className="space-y-4">
            <input
              type="password"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Senha de acesso"
              className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:border-[#C9A227] focus:ring-2 focus:ring-[#C9A227]/30 outline-none text-slate-700"
            />
            <button type="submit" className="w-full py-3 rounded-lg bg-[#1976D2] hover:bg-[#1565c0] text-white font-bold transition-colors">
              Entrar
            </button>
          </form>
        </div>
      </main>
    );
  }

  return (
    <main className="max-w-6xl mx-auto px-4 md:px-5 pt-10 pb-24 fade-in">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-700">Painel do Robô</h1>
          <div className="mt-2 h-1 w-20 rounded-full gold-underline" />
        </div>
        <div className="flex items-center gap-3">
          <button onClick={syncNow} disabled={syncing} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#C9A227] hover:bg-[#b8921f] text-white text-sm font-bold transition-colors disabled:opacity-60">
            {syncing ? <Loader2 className="animate-spin" size={16} /> : <RefreshCw size={16} />}
            {syncing ? "Sincronizando..." : "Sincronizar agora"}
          </button>
          <button onClick={logout} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 text-sm font-bold transition-colors">
            <LogOut size={16} /> Sair
          </button>
        </div>
      </div>

      <p className="text-sm text-slate-400 mb-6">
        Última sincronização: <b className="text-slate-600">{fmtDate(overview?.last_sync)}</b>
        {overview?.running && <span className="ml-2 text-green-600 font-semibold">• rodando agora</span>}
      </p>

      {loading && !overview ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#C9A227]" size={30} /></div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <Stat icon={Building2} label="Total de cotas" value={overview?.total ?? "—"} accent="#1976D2" />
            <Stat icon={HomeIcon} label="Imóvel" value={overview?.total_imovel ?? "—"} accent="#C9A227" />
            <Stat icon={Car} label="Veículo" value={overview?.total_veiculo ?? "—"} accent="#1976D2" />
            <Stat icon={TrendingUp} label="Administradoras" value={overview?.administradoras ?? "—"} accent="#C9A227" />
          </div>

          <div className="grid grid-cols-2 gap-4 mb-8">
            <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm flex items-center gap-3">
              <div className="h-11 w-11 rounded-xl bg-green-100 flex items-center justify-center"><Plus className="text-green-600" size={22} /></div>
              <div><div className="text-2xl font-extrabold text-green-600">{overview?.last_added ?? 0}</div><div className="text-xs text-slate-400">Adicionadas (última sync)</div></div>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm flex items-center gap-3">
              <div className="h-11 w-11 rounded-xl bg-red-100 flex items-center justify-center"><Minus className="text-red-500" size={22} /></div>
              <div><div className="text-2xl font-extrabold text-red-500">{overview?.last_removed ?? 0}</div><div className="text-xs text-slate-400">Removidas (última sync)</div></div>
            </div>
          </div>

          <h2 className="text-lg font-bold text-slate-700 mb-3">Histórico de sincronizações</h2>
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 text-slate-500 text-left">
                    <th className="px-4 py-3 font-semibold">Data / Hora</th>
                    <th className="px-4 py-3 font-semibold">Total</th>
                    <th className="px-4 py-3 font-semibold">Imóvel</th>
                    <th className="px-4 py-3 font-semibold">Veículo</th>
                    <th className="px-4 py-3 font-semibold text-green-600">Adicionadas</th>
                    <th className="px-4 py-3 font-semibold text-red-500">Removidas</th>
                  </tr>
                </thead>
                <tbody>
                  {history.length === 0 ? (
                    <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">Nenhuma sincronização registrada ainda.</td></tr>
                  ) : (
                    history.map((h, i) => (
                      <tr key={i} className="border-t border-slate-100 hover:bg-slate-50/60">
                        <td className="px-4 py-3 text-slate-600 font-medium">{fmtDate(h.last_sync)}</td>
                        <td className="px-4 py-3 text-slate-600">{h.total}</td>
                        <td className="px-4 py-3 text-slate-500">{h.total_imovel ?? "—"}</td>
                        <td className="px-4 py-3 text-slate-500">{h.total_veiculo ?? "—"}</td>
                        <td className="px-4 py-3"><span className="font-bold text-green-600">+{h.added}</span></td>
                        <td className="px-4 py-3"><span className="font-bold text-red-500">-{h.removed}</span></td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </main>
  );
}
